import GLightbox from '../../../../spine-appkit-vue/src/assets/vendor/glightbox/dist/js/glightbox';

/**
 * Manage shared GLightbox instances.
 *
 * Each previewGalleryId receives one GLightbox instance. Media cards without
 * a previewGalleryId receive a temporary single-item lightbox.
 */
 export default class MediaCardPreviewGallery {
    static galleries = new Map();

    /**
     * Get or create a gallery.
     *
     * @param {string} galleryId
     * @returns {{items: Map, lightbox: object}}
     */
    static getGallery(galleryId) {
        if (!this.galleries.has(galleryId)) {
            this.galleries.set(galleryId, {
                items: new Map(),
                lightbox: GLightbox({
                    elements: [],
                    openEffect: 'fade',
                    closeEffect: 'fade'
                }),
            });
        }

        return this.galleries.get(galleryId);
    }

    /**
     * Register or update an item in a gallery.
     *
     * @param {string} galleryId
     * @param {object} item
     */
    static register(galleryId, item) {
        if (!galleryId || !item.objectId || !item.type) {
            return;
        }

        const gallery = this.getGallery(galleryId);

        gallery.items.set(item.objectId, item);

        this.updateLightbox(gallery);
    }

    /**
     * Remove an item from a gallery.
     *
     * @param {string} galleryId
     * @param {string} objectId
     */
    static unregister(galleryId, objectId) {
        if (!galleryId || !objectId) {
            return;
        }

        const gallery = this.galleries.get(galleryId);

        if (!gallery) {
            return;
        }

        gallery.items.delete(objectId);

        if (gallery.items.size === 0) {
            gallery.lightbox.destroy();
            this.galleries.delete(galleryId);

            return;
        }

        this.updateLightbox(gallery);
    }

    /**
     * Update the slides of an existing GLightbox instance.
     *
     * @param {{items: Map, lightbox: object}} gallery
     */
    static updateLightbox(gallery) {
        const elements = Array
                .from(gallery.items.values())
                .map(item => this.createElement(item));

        gallery.lightbox.setElements(elements);
    }

    /**
     * Open a gallery at a specific media item.
     *
     * @param {string} galleryId
     * @param {string} objectId
     */
    static open(galleryId, objectId) {
        const gallery = this.galleries.get(galleryId);

        if (!gallery) {
            return;
        }

        const items = Array.from(gallery.items.values());
        const activeIndex = items.findIndex(
                item => item.objectId === objectId,
        );

        if (activeIndex === -1) {
            return;
        }

        gallery.lightbox.openAt(activeIndex);
    }

    /**
     * Open a standalone preview that does not belong to a gallery.
     *
     * @param {object} item
     */
    static openSingle(item) {
        const lightbox = GLightbox({
            elements: [
                this.createElement(item),
            ],
            oneSlidePerOpen: true,
            openEffect: 'fade',
            closeEffect: 'fade'
        });

        lightbox.on('close', () => {
            lightbox.destroy();
        });

        lightbox.open();
    }

    /**
     * Convert a media item into a GLightbox element.
     *
     * @param {object} item
     * @returns {object}
     */
    static createElement(item) {
        const isExternal = item.type === 'external';

        return {
            href: item.href,
            type: item.type,
            title: item.title || '',
            width: isExternal ? '75vw' : undefined,
            height: isExternal ? '75vh' : undefined,
        };
    }
}